import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { RouteComponentProps } from '../../../helpers/withRouter'
import Select from 'react-select'
import { AnyAction } from 'redux'
import { Button, Page, ValidatedInput } from '../../../components'
import CountryPickerModal from '../../../containers/CountryPickerModal'
import { connectRedux } from '../../../helpers/connectRedux'
import countries from '../../../countries.json'
import { isCodiceValid, isDniValid, isEmailValid, isTelephoneValid, isTextValid } from '../../../helpers'
import { appsDocumentSelected, appsRegisterSelected, appsStyleSelected } from '../../../helpers/app'
import { images } from '../../../images'
import { AuthenticatedRoutable, IDocument, IGenderType } from '../../../models'
import { ICountry } from '../../../models/country'
import literalsService from '../../../services/literals-service'
import {
  deleteDocumentAction,
  saveUserInfoAction,
  setNotificationAction,
  setUserInfoAdditionalPhoneAction,
  setUserInfoAdditionalPhoneCallingCodeAction,
  setUserInfoAdditionalPhoneCountryAction,
  setUserInfoBirthDayAction,
  setUserInfoBirthMonthAction,
  setUserInfoBirthYearAction,
  setUserInfoDNIAction,
  setUserInfoEmailAction,
  setUserInfoGenderAction,
  setUserInfoIsPrmAction,
  setUserInfoLastNameAction,
  setUserInfoNameAction,
  setUserInfoSettlementAction,
  uploadDocumentAction
} from '../../../store/actions'
import { IStoreState, IUserInfoState } from '../../../store/states'
import StyledArticle from './styles'

const registerData = appsRegisterSelected?.data || []

interface IProps extends IUserInfoState, DispatchProp<AnyAction>, RouteComponentProps {}

interface IState {
  isAddingDocument: boolean
  documentType: string
  documentFileName: string
  documentImage: string
}

class UserInfo extends AuthenticatedRoutable<IProps, IState> {

  public state: IState = {
    isAddingDocument: false,
    documentType: '',
    documentFileName: '',
    documentImage: ''
  }

  constructor(props: IProps) {
    super(props)
    if (registerData.includes('additionalPhone') && !props.additionalPhoneCountry) {
      const defaultCountry = countries.find(country => country.code === 'ES')
      if (defaultCountry) {
        props.dispatch(setUserInfoAdditionalPhoneCountryAction(defaultCountry))
        props.dispatch(setUserInfoAdditionalPhoneCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get genderOptions() {
    return Object.values(IGenderType).map(value => ({
      value,
      label: literalsService.get(value.toLowerCase(), true)
    }))
  }

  private get birthdateFields(): ReactNode {
    const { birthDay, birthMonth, birthYear } = this.props
    return (
      <div className="birthdate-row">
        <ValidatedInput
          name="birthDay"
          label={ literalsService.get('day', true) }
          value={ birthDay }
          onChange={ this.onBirthDayChange }
          error={ !birthDay }
        />
        <ValidatedInput
          name="birthMonth"
          label={ literalsService.get('month', true) }
          value={ birthMonth }
          onChange={ this.onBirthMonthChange }
          error={ !birthMonth }
        />
        <ValidatedInput
          name="birthYear"
          label={ literalsService.get('year', true) }
          value={ birthYear }
          onChange={ this.onBirthYearChange }
          error={ !birthYear }
        />
      </div>
    )
  }

  private get documentTypeOptions() {
    return (appsDocumentSelected?.documentType || []).map(value => ({
      value,
      label: literalsService.get(value, true)
    }))
  }

  public render(): ReactNode {

    const { firstName, lastName, email, dni, gender, settlement, prm, additionalPhone, additionalPhoneCountry, documents } = this.props
    const { isAddingDocument, documentType, documentFileName } = this.state
    const showDNI = process.env.REACT_APP_SHOW_DNI === 'true'

    return (
      <Page>
        <Page className={ `authenticated ${appsStyleSelected?.style}` }>
          <StyledArticle>
            <img src={ images['goBack'] } className='backButton' onClick={ this.handleGoBack } />
            <form onSubmit={ e => e.preventDefault() }>
              <section>
                <h3>{ literalsService.get('personalInfo', true) }</h3>
                <ValidatedInput
                  name="firstName"
                  label={ literalsService.get('name', true) }
                  value={ firstName }
                  onChange={ this.onFirstNameChange }
                  error={ !isTextValid(firstName) }
                />
                <ValidatedInput
                  name="lastName"
                  label={ literalsService.get('lastName', true) }
                  value={ lastName }
                  onChange={ this.onLastNameChange }
                  error={ !isTextValid(lastName) }
                />
                { registerData.includes('birthdate') && this.birthdateFields }
                { registerData.includes('gender') &&
                  <Select
                    className="select"
                    options={ this.genderOptions }
                    placeholder={ literalsService.get('gender', true) }
                    onChange={ this.onGenderChange }
                    value={ gender ? this.genderOptions.find(option => option.value === gender) : null }
                  />
                }
                { registerData.includes('village') &&
                  <ValidatedInput
                    name="settlement"
                    label={ literalsService.get('village', true) }
                    value={ settlement }
                    onChange={ this.onSettlementChange }
                    error={ !isTextValid(settlement) }
                  />
                }
                { registerData.includes('isPrm') &&
                  <div className="prm-container" onClick={ () => this.onIsPrmChange(!prm) }>
                    <img src={ images[ prm ? 'checkBoxFilled' : 'checkBoxEmpty' ] } alt="" />
                    <span>{ literalsService.get('reducedMobilityPerson', true) }</span>
                  </div>
                }
                { registerData.includes('additionalPhone') &&
                  <ValidatedInput
                    name="additionalPhone"
                    phone
                    label={ literalsService.get('additionalPhone', true) }
                    value={ additionalPhone }
                    country={ additionalPhoneCountry }
                    onChange={ this.onAdditionalPhoneChange }
                    error={ !!additionalPhone && !isTelephoneValid(additionalPhone) }
                  />
                }
                { showDNI ?
                  <ValidatedInput
                    name="dni"
                    label={ literalsService.get('dni', true) }
                    value={ dni }
                    error={ !(isDniValid(dni) || isCodiceValid(dni)) }
                    onChange={ this.onDNIChange }
                    isReadonly
                  />
                : null }
                <h3>{ literalsService.get('userInfo', true) }</h3>
                <ValidatedInput
                  name="email"
                  label={ literalsService.get('email', true) }
                  value={ email }
                  error={ !isEmailValid(email) }
                  onChange={ this.onEmailChange }
                  isReadonly
                />
              </section>
              { appsDocumentSelected?.requireDocumentation &&
                <section className="documents-section">
                  <h3>{ literalsService.get('uploadedDocuments', true) }</h3>
                  <div className="document-tags">
                    { documents.map((document: IDocument) => (
                      <div className="document-tag" key={ document.id }>
                        <span>{ literalsService.get(document.type, true) }</span>
                        <img src={ images['cross'] } alt="" onClick={ () => this.onDeleteDocument(document.id) } />
                      </div>
                    )) }
                    <div className="document-tag add" onClick={ this.toggleAddDocument }>
                      <img src={ images['plus'] } alt="" />
                    </div>
                  </div>
                  { isAddingDocument &&
                    <div className="document-upload-form">
                      <p>{ literalsService.get('documentTypeTitle', true) }</p>
                      <Select
                        className="select"
                        options={ this.documentTypeOptions }
                        placeholder={ literalsService.get('documentType', true) }
                        onChange={ this.onDocumentTypeChange }
                        value={ documentType ? this.documentTypeOptions.find(option => option.value === documentType) : null }
                      />
                      <input type="file" accept="image/*,.pdf" onChange={ this.onDocumentFileChange } />
                      <Button
                        title={ literalsService.get('uploadAFile', true) }
                        onPress={ this.uploadDocument }
                        isDisabled={ !documentType || !documentFileName }
                        className="uploadButton"
                        filled
                        upper
                      />
                    </div>
                  }
                </section>
              }
              { registerData.includes('additionalPhone') && <CountryPickerModal selectCountry={ this.onAdditionalPhoneCountryChange } /> }
              <Button
                title={ literalsService.get('save', true) }
                onPress={ this.save }
                className={ 'saveButton' }
                filled
                upper
              />
            </form>
          </StyledArticle>
        </Page>
      </Page>
    )
  }

  componentDidMount() {
    super.componentDidMount()
  }

  private handleGoBack = (): void => {
    history.back()
  }

  public save = (): void => {
    const { dispatch } = this.props
    dispatch(saveUserInfoAction())
    dispatch(setNotificationAction({
      label: literalsService.get('dataSaved', true),
      isError: false
    }))
    this.handleGoBack()
  }

  private onFirstNameChange = (name: string) => {
    this.props.dispatch(setUserInfoNameAction(name))
  }

  private onLastNameChange = (lastName: string) => {
    this.props.dispatch(setUserInfoLastNameAction(lastName))
  }

  private onDNIChange = (dni: string) => {
    this.props.dispatch(setUserInfoDNIAction(dni))
  }

  private onEmailChange = (email: string) => {
    this.props.dispatch(setUserInfoEmailAction(email))
  }

  private onBirthDayChange = (birthDay: string) => {
    this.props.dispatch(setUserInfoBirthDayAction(birthDay))
  }

  private onBirthMonthChange = (birthMonth: string) => {
    this.props.dispatch(setUserInfoBirthMonthAction(birthMonth))
  }

  private onBirthYearChange = (birthYear: string) => {
    this.props.dispatch(setUserInfoBirthYearAction(birthYear))
  }

  private onGenderChange = (option: any) => {
    this.props.dispatch(setUserInfoGenderAction(option.value))
  }

  private onSettlementChange = (settlement: string) => {
    this.props.dispatch(setUserInfoSettlementAction(settlement))
  }

  private onIsPrmChange = (isPrm: boolean) => {
    this.props.dispatch(setUserInfoIsPrmAction(isPrm))
  }

  private onAdditionalPhoneChange = (phone: string) => {
    this.props.dispatch(setUserInfoAdditionalPhoneAction(phone))
  }

  private onAdditionalPhoneCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setUserInfoAdditionalPhoneCountryAction(country))
    dispatch(setUserInfoAdditionalPhoneCallingCodeAction(country.dial_code))
  }

  private toggleAddDocument = () => {
    this.setState(state => ({ isAddingDocument: !state.isAddingDocument, documentType: '', documentFileName: '', documentImage: '' }))
  }

  private onDocumentTypeChange = (option: any) => {
    this.setState({ documentType: option.value })
  }

  private onDocumentFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      this.setState({ documentImage: reader.result as string, documentFileName: file.name })
    }
    reader.readAsDataURL(file)
  }

  private uploadDocument = () => {
    const { documentType, documentImage, documentFileName } = this.state
    if (!documentType || !documentImage) return
    this.props.dispatch(uploadDocumentAction(documentImage, documentType, documentFileName))
    this.setState({ isAddingDocument: false, documentType: '', documentFileName: '', documentImage: '' })
  }

  private onDeleteDocument = (id: number) => {
    this.props.dispatch(deleteDocumentAction(id))
  }
}

const mapStateToProps = ({ userInfo }: IStoreState) => ({
  ...userInfo
})

export default connectRedux(mapStateToProps, UserInfo)
